import React, { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Routes, Route, Outlet, Navigate } from "react-router-dom";
import { Container, Box } from "@mui/material";
import CirclesLoader from "../components/loader/CirclesLoader";

import PageLocationVerification from "../pages/auth/LocationVerification";

import MainLayout from "../layouts/MainLayout";
import SubPageLayout from "../layouts/SubPageLayout";

import PageUnderReview from "../layouts/pages/UnderReview";

/** Main Pages */
import PageForYou from "../pages/ForYou/ForYou";
import PageCreators from "../pages/Creators/Creators";
import PageStreams from "../pages/Streams/PageStreams";

/** Sub Pages */
import PageMyVideos from "../pages/MyVideos/PageMyVideos";
import PageMyCollections from "../pages/MyCollections/PageMyCollections";
import PageMyAssetWidgetEditor from "../pages/MyAssetWidgetEditor/MyAssetWidgetEditor";
import PageMore from "../pages/More/PageMore";
import PageGroups from "../pages/Groups/PageGroups";
import PageMyGroups from "../pages/MyGroups/PageMyGroups";
import PageMyProfile from "../pages/MyProfile/PageMyProfile";
import PageMyBookmarks from "../pages/Bookmarks/PageMyBookmarks";
import PageBookmarksPlay from "../pages/BookmarksPlay/PageBookmarksPlay";
import PageCreatorDetail from "../pages/CreatorDetail/PageCreatorDetail";
import PageChannels from "../pages/Channels/PageChannels";
import PageDesc from "../pages/Desc/Desc";
import PageCampaign from "../pages/Campaign/PageCampaign";

import { getAllChannels } from "../store/Channel.slice";
import { getAllAssets } from "../store/Asset.slice";
import { getAllDomains } from "../store/Domain.slice";
import { getAllMenus } from "../store/Menu.slice";
import { getAllUserFolders } from "../store/UserFolder.slice";
import SecondaryPageLayout from "../layouts/SecondaryPageLayout";
import PageCollections from "../pages/Collections/PageCollections";
import { getAllUsers } from "../store/User.slice";
import PageGroupDetail from "../pages/GroupDetail/PageGroupDetail";
import { getAllCategories } from "../store/Category.slice";
import PageAssetCardDetail from "../pages/Bookmarks/AssetCardDetail/PageAssetCardDetail";
import { getAllLocations } from "../store/Location.slice";
import { parseDomainAndLocation } from "../utils";
import { apiUpdateUser } from "../graphQL/user";
import { updateCurrentUser } from "../store/auth.slice";
import { actionUpdateUser } from "../store/User.slice";
import { GUEST } from "../config";
import { myToast } from "../libs";

const params = {
  ordering: {
    field: "rank",
    order: "Desc",
  },
  pagination: {
    page: 1,
    limit: 200,
  },
};

const getMediaAssetsErrorDetails = (err) => {
  if (!err) return "Unknown error";
  if (err?.debugDetail) return err.debugDetail;

  const messages = [];
  const visited = new Set();

  const pushMessage = (value, code) => {
    if (!value || typeof value !== "string") return;
    const message = value.trim();
    if (!message) return;
    const withCode = code ? `${message} (${code})` : message;
    if (!messages.includes(withCode)) {
      messages.push(withCode);
    }
  };

  const walk = (value, depth = 0) => {
    if (!value || depth > 4) return;
    if (typeof value === "string") {
      const text = value.trim();
      if (!text) return;
      if ((text.startsWith("{") && text.endsWith("}")) || (text.startsWith("[") && text.endsWith("]"))) {
        try {
          walk(JSON.parse(text), depth + 1);
          return;
        } catch (_) {
          // keep raw string if not valid json
        }
      }
      pushMessage(text);
      return;
    }
    if (typeof value !== "object") return;
    if (visited.has(value)) return;
    visited.add(value);

    if (Array.isArray(value)) {
      value.forEach((item) => walk(item, depth + 1));
      return;
    }

    if (Array.isArray(value.errors)) {
      value.errors.forEach((error) => {
        if (error && typeof error === "object") {
          pushMessage(error.message, error?.extensions?.code);
        }
        walk(error, depth + 1);
      });
    }

    if (value.message) {
      pushMessage(value.message, value?.extensions?.code);
    }

    if (value.bodyText) walk(value.bodyText, depth + 1);
    if (value.result) walk(value.result, depth + 1);
    if (value.networkError) walk(value.networkError, depth + 1);
    if (value.cause) walk(value.cause, depth + 1);
    if (value.error) walk(value.error, depth + 1);
    if (value.graphQLErrors) walk(value.graphQLErrors, depth + 1);
  };

  walk(err);

  const nonGenericMessages = messages.filter(
    (message) => !message.toLowerCase().includes("response not successful: received status code")
  );
  if (nonGenericMessages.length > 0) return nonGenericMessages.join(" | ");
  if (messages.length > 0) return messages.join(" | ");

  const networkStatus = err?.networkError?.statusCode || err?.cause?.statusCode;
  const networkMessage = err?.networkError?.message || err?.cause?.message;
  if (networkStatus && networkMessage) return `${networkMessage} (status ${networkStatus})`;
  if (networkMessage) return networkMessage;

  if (err?.message) return err.message;
  if (err?.error?.message) return err.error.message;

  return "Unknown error";
};

// function for Router
function PrivateRoutesWrapper() {
  const dispatch = useDispatch();
  const PrivateInfo = useSelector((store) => store.auth.PrivateInfo);
  const hasShownMediaAssetsErrorRef = useRef(false);
  const isLoadingMediaAssetsRef = useRef(false);
  const requestedDomainRef = useRef(null);

  useEffect(() => {
    const { domain: urlDomain } = parseDomainAndLocation();

    if (urlDomain) {
      if (requestedDomainRef.current !== urlDomain) {
        requestedDomainRef.current = urlDomain;
        hasShownMediaAssetsErrorRef.current = false;
      }

      if (isLoadingMediaAssetsRef.current) return;
      isLoadingMediaAssetsRef.current = true;

      dispatch(getAllUsers({}));
      dispatch(
        getAllChannels({
          domain: urlDomain,
        })
      );
      dispatch(getAllCategories({
        domain: urlDomain,
      }));
      dispatch(
        getAllMenus({
          domain: urlDomain,
          ...params,
        })
      );
      dispatch(
        getAllAssets({
          domain: urlDomain,
          status: "Published",
          highlight: true,
          ...params,
        })
      )
        .unwrap()
        .catch((err) => {
          const details = getMediaAssetsErrorDetails(err);
          console.error("Failed to load media assets:", {
            domain: urlDomain,
            details,
            raw: err,
          });
          if (!hasShownMediaAssetsErrorRef.current) {
            hasShownMediaAssetsErrorRef.current = true;
            myToast.error(
              `Failed to load media assets for "${urlDomain}". Reason: ${details}. If this keeps happening, check domain access and sign in again.`,
              {
                copy: true,
                toastId: `media-assets-load-error-${urlDomain}`,
              }
            );
          }
        })
        .finally(() => {
          isLoadingMediaAssetsRef.current = false;
        });
    }
  }, [dispatch]);

  useEffect(() => {
    if (PrivateInfo?.id) {
      dispatch(getAllUserFolders({ owner: PrivateInfo?.id, ...params }));
    }
  }, [dispatch, PrivateInfo?.id]);

  return <Outlet></Outlet>;
}

function PrivateRoutes() {
  const dispatch = useDispatch();
  const authenticated = useSelector((store) => store.auth.authenticated);
  const PrivateInfo = useSelector((store) => store.auth.PrivateInfo);
  const domains = useSelector((store) => store.Domain.domains);
  const locations = useSelector((store) => store.Location.locations);
  const hasSetupDomain = useRef(false);

  useEffect(() => {
    dispatch(getAllDomains());
    dispatch(getAllLocations());
  }, [dispatch]);

  // Reset setup flag when user changes
  useEffect(() => {
    hasSetupDomain.current = false;
  }, [PrivateInfo?.id]);

  // Automatically set up domain from URL when user logs in
  useEffect(() => {
    // Skip if domain is already set or we've already attempted setup
    if (hasSetupDomain.current || (PrivateInfo?.domain && (PrivateInfo?.locationId || ["sysadmin", "superAdmin"].includes(PrivateInfo.role)))) {
      return;
    }

    if (PrivateInfo?.id && domains && locations) {
      const { domain: parsedDomain, locationName } = parseDomainAndLocation();
      
      // Find domain by parsed domain name
      const domain = domains?.find((t) => t.name === parsedDomain);
      
      if (!domain) {
        hasSetupDomain.current = true; // Mark as attempted even if domain not found
        return;
      }
      
      hasSetupDomain.current = true; // Mark as attempted
      
      if (PrivateInfo.name === GUEST.email) {
        // Find location by domain and location name (subdomain)
        const location = locations?.find((loc) => 
          loc.domain === domain.name && 
          loc.name.toLowerCase() === locationName?.toLowerCase()
        );
        
        dispatch(updateCurrentUser({
          ...PrivateInfo,
          domain: domain.name,
          access: domain.accessList,
          locationId: location?.id,
          activated: true,
        }));
      } else {
        // Find location by domain and location name (subdomain)
        const location = locations?.find((loc) => 
          loc.domain === domain.name && 
          loc.name.toLowerCase() === locationName?.toLowerCase()
        );
        
        let variables = {
          id: PrivateInfo?.id,
          domain: domain.name,
          access: domain.accessList,
          activated: true,
          role: PrivateInfo?.role || domain.userRole || 'sysadmin'
        };
        
        // Add locationId if location is found
        if (location) {
          variables.locationId = location.id;
        }
        
        apiUpdateUser(variables).then((res) => {
          dispatch(updateCurrentUser(res));
          dispatch(actionUpdateUser(res));
        });
      }
    }
  }, [PrivateInfo?.id, PrivateInfo?.domain, PrivateInfo?.locationId, PrivateInfo?.role, domains, locations, dispatch, PrivateInfo]);

  if (authenticated) {
    if (!PrivateInfo)
      return (
        <Box sx={{ width: "100%", height: "100%" }} display="flex" alignItems="center" justifyContent="center">
          <CirclesLoader></CirclesLoader>
        </Box>
      );
    // Only show LocationVerification if automatic setup failed (domain not found in URL)
    if (!PrivateInfo?.domain || (!PrivateInfo?.locationId && !["sysadmin", "superAdmin"].includes(PrivateInfo.role))) {
      // Check if we're still loading domains/locations
      if (!domains || !locations) {
        return (
          <Box sx={{ width: "100%", height: "100%" }} display="flex" alignItems="center" justifyContent="center">
            <CirclesLoader></CirclesLoader>
          </Box>
        );
      }
      // If domains/locations are loaded but domain still not set, show verification as fallback
      return <PageLocationVerification></PageLocationVerification>;
    }
    if (!PrivateInfo?.activated) return <PageUnderReview></PageUnderReview>;
  }

  return (
    <Container maxWidth="lg" sx={{ height: "100%", px: { xs: 0, sm: 0, md: 0 }, position: "relative" }}>
      <Routes>
        <Route path="" element={<PrivateRoutesWrapper></PrivateRoutesWrapper>}>
          <Route path="/" element={<MainLayout></MainLayout>}>
            <Route path="" index element={<PageForYou></PageForYou>}></Route>
            <Route path="campaign" element={<PageCampaign></PageCampaign>}></Route>
            {/* <Route path="streams" element={<PageStreams></PageStreams>}></Route> */}
          </Route>
          <Route path="/" element={<SecondaryPageLayout></SecondaryPageLayout>}>
            <Route path="more" element={<PageMore></PageMore>}></Route>
            <Route path="profile" element={<PageMyProfile></PageMyProfile>}></Route>
            <Route path="stuffs/play/:userFolderId" element={<PageBookmarksPlay></PageBookmarksPlay>}></Route>
            <Route path="stuffs" element={<PageMyBookmarks></PageMyBookmarks>}></Route>
            <Route path="myvideos" element={<PageMyVideos></PageMyVideos>}></Route>
            <Route path="mygroups" element={<PageMyGroups></PageMyGroups>}></Route>
            <Route path="mycollections" element={<PageMyCollections></PageMyCollections>}></Route>
            <Route path="group/detail/:userGroupId" element={<PageGroupDetail></PageGroupDetail>}></Route>
            <Route path="creator/play/:creatorId" element={<PageCreatorDetail></PageCreatorDetail>}></Route>
            <Route path="asset/play/:assetId" element={<PageAssetCardDetail></PageAssetCardDetail>}></Route>
            <Route path="bookmarks" element={<Navigate to="/stuffs"></Navigate>}></Route>
            <Route path="*" element={<Navigate to="/"></Navigate>}></Route>
          </Route>
          <Route path="/" element={<SubPageLayout></SubPageLayout>}>
            <Route path="creators" element={<PageCreators></PageCreators>}></Route>
            <Route path="collections" element={<PageCollections></PageCollections>}></Route>
            <Route path="channels" element={<PageChannels></PageChannels>}></Route>
            <Route path="groups" element={<PageGroups></PageGroups>}></Route>
            <Route path="desc" element={<PageDesc></PageDesc>}></Route>
            <Route path="asset/:assetId/widgets" element={<PageMyAssetWidgetEditor></PageMyAssetWidgetEditor>}></Route>
            <Route path="*" element={<Navigate to="/"></Navigate>}></Route>
          </Route>
        </Route>
      </Routes>
    </Container>
  );
}

export default PrivateRoutes;
